import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";

const DROPDOWN_MIN_WIDTH = 160;

const MoreVerticalIcon = ({ className = '', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={`w-5 h-5 ${className}`}
    {...props}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
    />
  </svg>
);

const Table = ({
  columns = [],
  data = [],
  onRowClick,
  selectable = false,
  selectedRows = [],
  onSelectRow,
  onSelectAll,
  selectAllChecked,
  selectAllIndeterminate = false,
  selectAllLoading = false,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  actions = [],
  getRowClassName,
  defaultRowsPerPage = 5,
  rowsPerPageOptions = [5, 10, 15, 20],
  groupedHeaders = null,
  reportTable = false,
  /** When true, render all rows; parent handles API pagination (reports). */
  serverPaginated = false,
}) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const [dropdownAnchorRect, setDropdownAnchorRect] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(
    Number(defaultRowsPerPage) > 0 ? Number(defaultRowsPerPage) : 5
  );
  const dropdownContentRef = useRef(null);

  const isAllSelected =
    typeof selectAllChecked === 'boolean'
      ? selectAllChecked
      : data.length > 0 && selectedRows.length === data.length;
  const hasActions =
    typeof actions === 'function' ? true : actions && actions.length > 0;
  const totalCols = columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0);

  // Pagination logic
  const totalItems = data.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedData = serverPaginated
    ? data
    : data.slice(startIndex, endIndex);

  // Reset to page 1 when data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [data.length]);

  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));
    if (currentPage > newTotalPages) {
      setCurrentPage(1);
    }
  }, [data.length, rowsPerPage]);

  useEffect(() => {
    const next = Number(defaultRowsPerPage);
    if (Number.isFinite(next) && next > 0) {
      setRowsPerPage(next);
      setCurrentPage(1);
    }
  }, [defaultRowsPerPage]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const portalEl = dropdownContentRef.current;
      if (portalEl && portalEl.contains(event.target)) return;
      setOpenDropdown(null);
      setDropdownAnchorRect(null);
    };

    if (openDropdown !== null) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdown]);

  // Close dropdown on scroll so position doesn't get stale
  useEffect(() => {
    if (openDropdown === null) return;
    const handleScroll = () => {
      setOpenDropdown(null);
      setDropdownAnchorRect(null);
    };
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [openDropdown]);

  const handleSelectAll = () => {
    if (selectAllLoading) return;
    if (isAllSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(data.map((_, index) => index));
    }
  };

  const handleSelectRow = (index, e) => {
    if (selectAllLoading) return;
    e.stopPropagation();
    const newSelected = selectedRows.includes(index)
      ? selectedRows.filter((i) => i !== index)
      : [...selectedRows, index];
    onSelectRow(newSelected);
  };

  const alignClass = (align) => {
    if (align === 'center') return 'text-center';
    if (align === 'right') return 'text-right';
    return 'text-left';
  };

  const thClass = (colIndex, { compact = false } = {}) => {
    const align = alignClass(columns[colIndex - (selectable ? 1 : 0)]?.align);
    const first = colIndex === 0 ? 'rounded-l-[12px] ' : '';
    const last = colIndex === totalCols - 1 ? 'rounded-r-[12px] ' : '';
    const heightClass = compact ? 'h-10' : 'h-14';
    const weightClass = reportTable ? 'font-bold' : 'font-semibold';
    return `${first}${last}${align} ${heightClass} whitespace-nowrap border-none ${weightClass} text-sm`;
  };

  const thStyle = reportTable
    ? {
        border: 'none',
        borderBottom: '1px solid #E4E7EC',
        backgroundColor: '#F2F4F7',
        color: '#344054',
        paddingLeft: 22,
        paddingRight: 22,
      }
    : {
        border: 'none',
        backgroundColor: '#F6F7FA',
        color: '#98A2B3',
        paddingLeft: 22,
        paddingRight: 22,
      };

  const tdClass = (colIndex, rowIndex, row) => {
    const selected = selectedRows.includes(rowIndex);
    const rowClassName = getRowClassName ? getRowClassName(row, rowIndex) : '';
    const isHighlighted = rowClassName && rowClassName.includes('highlight');

    const bg = selected ? 'bg-blue-50' : isHighlighted ? 'bg-yellow-50' : 'bg-transparent';
    const align = alignClass(columns[colIndex - (selectable ? 1 : 0)]?.align);
    const first = colIndex === 0 ? 'rounded-l-lg ' : '';
    const last = colIndex === totalCols - 1 ? 'rounded-r-lg ' : '';
    return `${first}${last}${align} ${bg} whitespace-nowrap border-none text-base font-medium h-16`;
  };

  if (loading) {
    return (
      <div
        className={`wms-pill-table-loading flex justify-center items-center p-8 bg-transparent ${className}`}
        style={{ color: '#161616' }}
        role="status"
        aria-live="polite"
      >
        Loading...
      </div>
    );
  }

  return (
    <div
      className={`wms-pill-table-root w-full overflow-visible font-nunito text-[#161616] ${className}`}
      style={{ backgroundColor: 'transparent', padding: 0 }}
      role="region"
      aria-label="Data table"
    >
      <div
        className="wms-pill-table-card overflow-visible"
        style={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #EEF1F5',
          borderRadius: 16,
          boxShadow: '0 2px 10px rgba(16,24,40,0.04)',
          padding: 18,
        }}
      >
        <div className="wms-pill-table-scroll overflow-x-auto overflow-y-visible">
          <table
            className="wms-pill-table w-full text-[#161616]"
            style={{ borderSpacing: '0 14px', borderCollapse: 'separate', border: 'none' }}
          >
            <thead>
              {groupedHeaders ? (
                <>
                  <tr>
                    {selectable && (
                      <th
                        rowSpan={2}
                        className={`rounded-l-[12px] h-14 whitespace-nowrap w-10 md:w-12 border-none pl-[22px] pr-[22px] font-semibold text-base ${totalCols === 1 ? 'rounded-r-[12px]' : ''}`}
                        scope="col"
                        style={thStyle}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = Boolean(selectAllIndeterminate);
                            }}
                            onChange={handleSelectAll}
                            disabled={selectAllLoading}
                            className="w-4 h-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
                            aria-label="Select all rows"
                            aria-busy={selectAllLoading}
                          />
                          {selectAllLoading && (
                            <span
                              className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-[#188742]"
                              aria-hidden="true"
                            />
                          )}
                        </div>
                      </th>
                    )}

                    {columns.slice(0, groupedHeaders.baseColumnCount).map((column, index) => (
                      <th
                        key={column.key || index}
                        rowSpan={2}
                        className={thClass(selectable ? index + 1 : index)}
                        scope="col"
                        style={thStyle}
                      >
                        {column.label}
                      </th>
                    ))}

                    {groupedHeaders.groups.map((group, groupIndex) => (
                      <th
                        key={`${group.label}-${groupIndex}`}
                        colSpan={group.span}
                        className={`h-10 whitespace-nowrap border-none text-center text-base ${reportTable ? 'font-bold' : 'font-semibold'}`}
                        scope="colgroup"
                        style={thStyle}
                      >
                        {group.label}
                      </th>
                    ))}

                    {Array.from({ length: groupedHeaders.trailingUngroupedCount || 0 }).map((_, index) => (
                      <th
                        key={`ungrouped-${index}`}
                        className={`h-10 whitespace-nowrap border-none text-base ${reportTable ? 'font-bold' : 'font-semibold'}`}
                        scope="col"
                        style={thStyle}
                      />
                    ))}

                    {hasActions && (
                      <th
                        rowSpan={2}
                        className={`${thClass(selectable ? columns.length + 1 : columns.length)} text-right`}
                        scope="col"
                        style={thStyle}
                      >
                        Actions
                      </th>
                    )}
                  </tr>
                  <tr>
                    {columns.slice(groupedHeaders.baseColumnCount).map((column, index) => (
                      <th
                        key={column.key || index}
                        className={thClass(
                          selectable
                            ? groupedHeaders.baseColumnCount + index + 1
                            : groupedHeaders.baseColumnCount + index,
                          { compact: true }
                        )}
                        scope="col"
                        style={thStyle}
                      >
                        {column.label}
                      </th>
                    ))}
                  </tr>
                </>
              ) : (
                <tr>
                  {selectable && (
                    <th
                      className={`rounded-l-[12px] h-14 whitespace-nowrap w-10 md:w-12 border-none pl-[22px] pr-[22px] font-semibold text-base ${totalCols === 1 ? 'rounded-r-[12px]' : ''}`}
                      scope="col"
                      style={thStyle}
                    >
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) el.indeterminate = Boolean(selectAllIndeterminate);
                          }}
                          onChange={handleSelectAll}
                          disabled={selectAllLoading}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label="Select all rows"
                          aria-busy={selectAllLoading}
                        />
                        {selectAllLoading && (
                          <span
                            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-[#188742]"
                            aria-hidden="true"
                          />
                        )}
                      </div>
                    </th>
                  )}

                  {columns.map((column, index) => (
                    <th
                      key={index}
                      className={thClass(selectable ? index + 1 : index)}
                      scope="col"
                      style={thStyle}
                    >
                      {column.label}
                    </th>
                  ))}

                  {hasActions && (
                    <th
                      className={`${thClass(selectable ? columns.length + 1 : columns.length)} text-right`}
                      scope="col"
                      style={thStyle}
                    >
                      Actions
                    </th>
                  )}
                </tr>
              )}
            </thead>

          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((row, rowIndex) => {
                const actualRowIndex = startIndex + rowIndex;
                const actionsList = (() => {
                  const rowActions = typeof actions === 'function' ? actions(row) : actions;
                  return Array.isArray(rowActions) ? rowActions : [];
                })();

                return (
                  <tr
                    key={actualRowIndex}
                    onClick={() => onRowClick && onRowClick(row, actualRowIndex)}
                    className={`group ${onRowClick ? 'cursor-pointer' : ''}`}
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                    onKeyDown={
                      onRowClick
                        ? (e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              onRowClick(row, actualRowIndex);
                            }
                          }
                        : undefined
                    }
                    aria-label={
                      onRowClick ? `Row ${actualRowIndex + 1}, click to select` : undefined
                    }
                  >
                    {selectable && (
                      <td
                        className={tdClass(0, actualRowIndex, row)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(actualRowIndex)}
                          onChange={(e) => handleSelectRow(actualRowIndex, e)}
                          disabled={selectAllLoading}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60"
                          aria-label={`Select row ${actualRowIndex + 1}`}
                        />
                      </td>
                    )}

                    {columns.map((column, colIndex) => (
                      <td
                        key={colIndex}
                        className={tdClass(selectable ? colIndex + 1 : colIndex, actualRowIndex, row)}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        {column.render
                          ? column.render(row[column.key], row, actualRowIndex)
                          : row[column.key]}
                      </td>
                    ))}

                    {hasActions && (
                      <td
                        className={tdClass(
                          selectable ? columns.length + 1 : columns.length,
                          actualRowIndex,
                          row
                        )}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          border: 'none',
                          borderBottom: '1px solid #F2F4F7',
                          paddingLeft: 22,
                          paddingRight: 22,
                        }}
                      >
                        {actionsList.length > 0 ? (
                          <div
                            className="relative flex items-center justify-end transition-colors"
                            style={{ gap: 22 }}
                          >
                            {(() => {
                              const alwaysVisibleActions = actionsList.filter(
                                (action) => action?.alwaysVisible
                              );
                              const regularActions = actionsList.filter(
                                (action) => !action?.alwaysVisible
                              );
                              const inlineActions = [
                                ...alwaysVisibleActions,
                                ...regularActions.slice(0, 2),
                              ];
                              return inlineActions;
                            })().map((action, i) => {
                              const IconComponent = action.icon;
                              return (
                                <button
                                  key={i}
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    action.onClick(row, actualRowIndex);
                                  }}
                                  className="wms-table-action-btn grid place-items-center h-9 w-9 rounded-lg border-0 bg-transparent active:scale-[0.98] transition"
                                  aria-label={action.label}
                                  title={action.tooltip || action.label}
                                >
                                  {action.label?.toLowerCase().includes('delete') ? (
                                    <MdOutlineDelete className="w-5 h-5" />
                                  ) : action.label?.toLowerCase().includes('edit') ? (
                                    <FiEdit className="w-5 h-5" />
                                  ) : IconComponent ? (
                                    <IconComponent className="w-5 h-5" />
                                  ) : null}
                                </button>
                              );
                            })}

                            {(() => {
                              const regularActions = actionsList.filter(
                                (action) => !action?.alwaysVisible
                              );
                              return regularActions.length > 2;
                            })() && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openDropdown === actualRowIndex) {
                                    setOpenDropdown(null);
                                    setDropdownAnchorRect(null);
                                  } else {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setDropdownAnchorRect(rect);
                                    setOpenDropdown(actualRowIndex);
                                  }
                                }}
                                className="wms-table-action-btn flex h-9 w-9 items-center justify-center rounded-lg border-0 bg-transparent active:scale-[0.98] transition"
                                aria-label="More actions"
                              >
                                <MoreVerticalIcon className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        ) : null}
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={totalCols}
                  className="py-10 text-center text-base font-medium rounded-lg"
                  style={{ color: '#98A2B3', paddingLeft: 22, paddingRight: 22 }}
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!serverPaginated && (
        <div className="mt-6 flex items-center justify-between w-full">
          {/* LEFT: Rows per page */}
          <div className="flex items-center">
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm font-medium text-gray-700 shadow-sm"
            >
              {(Array.isArray(rowsPerPageOptions) && rowsPerPageOptions.length
                ? rowsPerPageOptions
                : [5, 10, 15, 20]
              )
                .filter((v) => Number(v) > 0)
                .map((v) => (
                  <option key={v} value={Number(v)}>
                    {Number(v)}
                  </option>
                ))}
            </select>
          </div>

          {/* CENTER: Page numbers */}
          <div className="flex items-center gap-2">
            {(() => {
              const pages = [];
              let lastShownPage = 0;

              for (let page = 1; page <= totalPages; page++) {
                const showPage =
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 1 && page <= currentPage + 1);

                if (showPage) {
                  if (page - lastShownPage > 1) {
                    pages.push(
                      <span key={`ellipsis-${page}`} className="text-gray-400 text-sm">
                        ...
                      </span>
                    );
                  }

                  pages.push(
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={
                        currentPage === page
                          ? 'h-8 w-8 rounded-md bg-gray-100 text-sm font-semibold text-gray-900 hover:bg-gray-50'
                          : 'h-8 w-8 text-sm text-gray-700 hover:bg-gray-50'
                      }
                      aria-label={`Page ${page}`}
                      aria-current={currentPage === page ? 'page' : undefined}
                    >
                      {page}
                    </button>
                  );

                  lastShownPage = page;
                }
              }

              return pages;
            })()}
          </div>

          {/* RIGHT: Prev / Next */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              ‹ Previous
            </button>

            <button
              type="button"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              Next ›
            </button>
          </div>
        </div>
      )}
      </div>

      {/* Dropdown (only when more than 2 actions exist) */}
      {openDropdown !== null &&
        dropdownAnchorRect &&
        createPortal(
          (() => {
            const row = data[openDropdown];
            const rowActions = typeof actions === 'function' ? actions(row) : actions;
            const actionsList = Array.isArray(rowActions) ? rowActions : [];
            const extraActions = actionsList.filter((action) => !action?.alwaysVisible).slice(2);

            const rect = dropdownAnchorRect;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < 220;

            const style = {
              position: 'fixed',
              left: Math.max(
                8,
                Math.min(rect.right - DROPDOWN_MIN_WIDTH, window.innerWidth - DROPDOWN_MIN_WIDTH - 8)
              ),
              width: DROPDOWN_MIN_WIDTH,
              zIndex: 99999,
              ...(openAbove ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 4 }),
            };

            return (
              <div
                ref={dropdownContentRef}
                className="min-w-[160px] rounded-xl border border-gray-200 bg-white shadow-lg py-1"
                style={style}
              >
                {extraActions.map((action, actionIndex) => {
                  const IconComponent = action.icon;
                  return (
                    <button
                      key={actionIndex}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        action.onClick(row, openDropdown);
                        setOpenDropdown(null);
                        setDropdownAnchorRect(null);
                      }}
                      className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-gray-700 transition hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl"
                    >
                      {IconComponent && (
                        <span className="text-gray-600">
                          <IconComponent className="w-4 h-4" />
                        </span>
                      )}
                      <span>{action.label}</span>
                    </button>
                  );
                })}
              </div>
            );
          })(),
          document.body
        )}
    </div>
  );
};

export default Table;
